import React, { useEffect, useState } from 'react';
import ArchetypeBadge from './ArchetypeBadge';
import Icon from './Icon';

const ARCHETYPE_META = {
  "The Dynasty Architect":   { color: '#ffd32a', icon: '🏛️', bg: 'rgba(255,211,42,0.06)',  border: 'rgba(255,211,42,0.22)' },
  "The Win-Now Mercenary":   { color: '#ff6b35', icon: '⚔️', bg: 'rgba(255,107,53,0.06)',  border: 'rgba(255,107,53,0.22)' },
  "The Deadline Raider":     { color: '#ff4757', icon: '⏰', bg: 'rgba(255,71,87,0.06)',   border: 'rgba(255,71,87,0.22)' },
  "The Trade Addict":        { color: '#a855f7', icon: '🔄', bg: 'rgba(168,85,247,0.06)',  border: 'rgba(168,85,247,0.22)' },
  "The Reluctant Rebuilder": { color: '#888888', icon: '🔧', bg: 'rgba(136,136,136,0.06)', border: 'rgba(136,136,136,0.22)' },
  "The Sleeper Cell":        { color: '#00d4ff', icon: '🥷', bg: 'rgba(0,212,255,0.06)',   border: 'rgba(0,212,255,0.22)' },
  "The Draft Evangelist":    { color: '#00e676', icon: '📋', bg: 'rgba(0,230,118,0.06)',   border: 'rgba(0,230,118,0.22)' },
  "The Chaos Agent":         { color: '#e040fb', icon: '🌪️', bg: 'rgba(224,64,251,0.06)',  border: 'rgba(224,64,251,0.22)' },
  "The Ironclad":            { color: '#4dc8e8', icon: '🛡️', bg: 'rgba(77,200,232,0.06)',  border: 'rgba(77,200,232,0.22)' },
  "The Legacy Builder":      { color: '#c8a200', icon: '👑', bg: 'rgba(200,162,0,0.06)',   border: 'rgba(200,162,0,0.22)' },
};

const SIGNAL_LABELS = {
  win_rate_pct:           { label: 'All-Time Win %',       unit: '%' },
  championships:          { label: 'Championships',         unit: '' },
  tenure_seasons:         { label: 'Seasons Active',        unit: '' },
  trade_activity_score:   { label: 'Trade Activity',        unit: '/10' },
  asset_philosophy_score: { label: 'Asset Philosophy',      unit: '/10' },
  contention_score:       { label: 'Contention Behavior',   unit: '/10' },
  roster_balance_score:   { label: 'Roster Balance',        unit: '/10' },
  waiver_claims:          { label: 'Waiver Claims',         unit: '' },
  picks_received:         { label: 'Picks Received',        unit: '' },
  picks_sent:             { label: 'Picks Sent',            unit: '' },
};

function SignalRow({ label, value, unit, max, color }) {
  const displayMax = max || 10;
  const pct = unit === '/10'
    ? Math.min(100, (value / 10) * 100)
    : unit === '%'
      ? Math.min(100, value)
      : null;
  const barColor = value >= displayMax * 0.7 ? 'var(--green)' : value >= displayMax * 0.4 ? '#00d4ff' : '#ffd32a';

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--text2)' }}>{label}</span>
        <span style={{ fontSize: '0.72rem', fontFamily: 'var(--font-mono)', color, fontWeight: 700 }}>
          {value}{unit}
        </span>
      </div>
      {pct !== null && (
        <div style={{ height: 4, background: 'var(--bg4)', borderRadius: 2, overflow: 'hidden' }}>
          <div style={{
            height: '100%', width: `${pct}%`,
            background: `linear-gradient(90deg, ${barColor}80, ${barColor})`,
            borderRadius: 2, transition: 'width 0.6s ease',
          }} />
        </div>
      )}
    </div>
  );
}

function HistoryEntry({ era }) {
  const meta = ARCHETYPE_META[era.archetype] || { color: '#888', icon: '•' };
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      paddingBottom: 10, borderBottom: '1px solid var(--border)',
    }}>
      <span style={{ fontSize: '1rem', lineHeight: 1, marginTop: 2 }}>{meta.icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.78rem', color: meta.color, fontWeight: 600, lineHeight: 1.2 }}>
          {era.archetype}
        </div>
        <div style={{ fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
          {era.start_season}
          {era.end_season && era.end_season !== era.start_season ? ` – ${era.end_season}` : ''}
          {era.duration_weeks ? ` · ${era.duration_weeks}w` : ''}
          {era.is_current ? ' · current' : ''}
        </div>
      </div>
    </div>
  );
}

/**
 * ArchetypeCard — full canonical archetype display.
 *
 * Props:
 *   userId   — Sleeper user_id (fetches from /api/archetypes/{userId})
 *   apiBase  — API base URL (e.g. "https://api.example.com")
 *   data     — optional pre-fetched profile dict (skips fetch when provided)
 */
export default function ArchetypeCard({ userId, apiBase, data: propData }) {
  const [profile, setProfile] = useState(propData || null);
  const [loading, setLoading] = useState(!propData && Boolean(userId));
  const [error, setError] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (propData) { setProfile(propData); return; }
    if (!userId || !apiBase) return;
    setLoading(true);
    fetch(`${apiBase}/api/archetypes/${userId}`)
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(d => { setProfile(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, [userId, apiBase, propData]);

  if (loading) {
    return (
      <div style={{ padding: '20px 24px', color: 'var(--text3)', fontSize: '0.78rem' }}>
        Loading archetype...
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div style={{ padding: '20px 24px', color: 'var(--text3)', fontSize: '0.78rem' }}>
        Archetype data unavailable.
      </div>
    );
  }

  const archetype = profile.archetype || 'The Ironclad';
  const meta = ARCHETYPE_META[archetype] || { color: '#888888', icon: '🎯', bg: 'rgba(136,136,136,0.06)', border: 'rgba(136,136,136,0.22)' };
  const signals = profile.signal_summary || {};
  const history = profile.archetype_history || [];
  const identity = (profile.archetype_meta || {}).identity || '';
  const tagline = (profile.archetype_meta || {}).tagline || '';
  const reasoning = profile.reasoning || '';

  const domainSignals = ['trade_activity_score', 'asset_philosophy_score', 'contention_score', 'roster_balance_score'];
  const statSignals = ['win_rate_pct', 'championships', 'tenure_seasons', 'waiver_claims', 'picks_received', 'picks_sent'];

  return (
    <div style={{
      background: meta.bg,
      border: `1px solid ${meta.border}`,
      borderRadius: 14,
      overflow: 'hidden',
      position: 'relative',
    }}>
      {/* Top accent bar */}
      <div style={{ height: 3, background: `linear-gradient(90deg, ${meta.color}, transparent)` }} />

      <div style={{ padding: '20px 24px' }}>

        {/* Header */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: '0.62rem', color: meta.color, letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 8, opacity: 0.85 }}>
            Canonical Archetype
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ lineHeight: 1 }}><Icon emoji={meta.icon} size={38} /></span>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', color: meta.color, letterSpacing: '0.5px', lineHeight: 1.1 }}>
                {archetype}
              </div>
              {identity && (
                <div style={{ fontSize: '0.72rem', color: 'var(--text3)', marginTop: 4 }}>{identity}</div>
              )}
            </div>
          </div>
          {tagline && (
            <div style={{
              fontSize: '0.78rem', color: 'var(--text2)', fontStyle: 'italic',
              marginTop: 10, paddingLeft: 4, borderLeft: `2px solid ${meta.color}44`,
              paddingLeft: 10,
            }}>
              "{tagline}"
            </div>
          )}
        </div>

        {/* Reasoning */}
        {reasoning && (
          <div style={{
            fontSize: '0.82rem', color: 'var(--text2)', lineHeight: 1.65,
            background: 'rgba(0,0,0,0.15)', borderRadius: 8,
            padding: '12px 14px', marginBottom: 18,
          }}>
            {reasoning}
          </div>
        )}

        {/* Domain signal bars */}
        {domainSignals.some(k => signals[k] !== undefined) && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: '0.6rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '1.5px', fontFamily: 'var(--font-mono)', marginBottom: 10 }}>
              Behavioral Signals
            </div>
            {domainSignals.map(k => signals[k] !== undefined ? (
              <SignalRow
                key={k}
                label={SIGNAL_LABELS[k]?.label || k}
                value={signals[k]}
                unit={SIGNAL_LABELS[k]?.unit || ''}
                color={meta.color}
              />
            ) : null)}
          </div>
        )}

        {/* Career stats */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 18 }}>
          {statSignals.filter(k => signals[k] !== undefined && signals[k] !== 0).map(k => (
            <div key={k} style={{
              background: 'var(--bg3)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '8px 12px', textAlign: 'center', minWidth: 64,
            }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: meta.color, lineHeight: 1 }}>
                {signals[k]}{SIGNAL_LABELS[k]?.unit || ''}
              </div>
              <div style={{ fontSize: '0.58rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.8px', fontFamily: 'var(--font-mono)', marginTop: 3 }}>
                {SIGNAL_LABELS[k]?.label || k}
              </div>
            </div>
          ))}
        </div>

        {/* Archetype history */}
        {history.length > 0 && (
          <div>
            <button
              onClick={() => setShowHistory(h => !h)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.65rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)',
                textTransform: 'uppercase', letterSpacing: '1.5px',
                display: 'flex', alignItems: 'center', gap: 6, padding: 0, marginBottom: 10,
              }}
            >
              <span>{showHistory ? '▼' : '▶'}</span>
              Archetype History ({history.length} era{history.length !== 1 ? 's' : ''})
            </button>
            {showHistory && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[...history].reverse().map((era, i) => (
                  <HistoryEntry key={i} era={era} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
